import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { propertiesAPI, Property } from '../api/api';
import PropertyCard from '../components/PropertyCard';

const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100 animate-pulse">
    <div className="aspect-[4/3] bg-gray-200"></div>
    <div className="p-4">
      <div className="h-4 bg-gray-200 rounded mb-2"></div>
      <div className="h-3 bg-gray-100 rounded w-2/3 mb-3"></div>
      <div className="flex justify-between">
        <div className="h-5 bg-gray-200 rounded w-1/3"></div>
        <div className="h-3 bg-gray-100 rounded w-1/4"></div>
      </div>
    </div>
  </div>
);

const HomePage: React.FC = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loadingProps, setLoadingProps] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'apartment' | 'house'>('all');

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      setLoadingProps(true);
      const response = await propertiesAPI.list({ status: 'active', page_size: 12 });
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to load properties', error);
    } finally {
      setLoadingProps(false);
    }
  };

  const filteredProperties = properties.filter(p => {
    const matchesFilter = activeFilter === 'all' || p.property_type === activeFilter;
    const matchesSearch = !searchQuery ||
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.address && p.address.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-10 w-48 h-48 bg-accent-400 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-20">
          <div className="text-center">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black mb-3 sm:mb-4 leading-tight tracking-tight">
              {t('heroTitle')}
            </h1>
            <p className="text-sm sm:text-lg mb-6 sm:mb-10 text-primary-100 max-w-2xl mx-auto font-medium">
              {t('heroDescription')}
            </p>

            {/* Search bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="flex bg-white rounded-2xl shadow-2xl overflow-hidden p-1 sm:p-1.5 border border-white/20">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('searchPlaceholder')}
                  className="flex-1 px-3 sm:px-5 py-3 sm:py-3.5 text-gray-800 text-sm sm:text-base focus:outline-none placeholder-gray-400"
                />
                <button
                  type="submit"
                  className="bg-primary-600 hover:bg-primary-500 text-white px-5 sm:px-8 py-3 sm:py-3.5 font-bold rounded-xl transition-all flex items-center shadow-md active:scale-95 text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="ml-2 hidden sm:inline">{t('findBtn')}</span>
                </button>
              </div>
            </form>

            {/* Quick stats */}
            <div className="flex justify-center space-x-6 sm:space-x-12 mt-8 sm:mt-10 text-xs sm:text-sm">
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-black">{properties.length}+</p>
                <p className="text-primary-200">{t('listingsCount')}</p>
              </div>
              <div className="w-px bg-white/20"></div>
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-black">14</p>
                <p className="text-primary-200">{t('citiesCount')}</p>
              </div>
              <div className="w-px bg-white/20"></div>
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-black">100%</p>
                <p className="text-primary-200">{t('verifiedCount')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Listings Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
              {t('freshListings')}
            </h2>
            <p className="text-gray-500 text-xs sm:text-sm mt-1">
              {loadingProps ? t('loading') : filteredProperties.length > 0
                ? `${filteredProperties.length} ${t('foundCount')}`
                : t('noPropertiesFound')}
            </p>
          </div>

          {/* Filter buttons */}
          <div className="flex bg-gray-200/70 p-1 rounded-xl self-start sm:self-auto gap-1">
            {[
              { key: 'all', label: t('allTypes') },
              { key: 'apartment', label: t('apartment') },
              { key: 'house', label: t('house') },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveFilter(key as typeof activeFilter)}
                className={`px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all ${
                  activeFilter === key
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Properties Grid */}
        {loadingProps ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : filteredProperties.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {filteredProperties.map(property => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/search"
                className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white font-bold px-8 py-3.5 rounded-xl transition-all shadow-md hover:shadow-lg active:scale-95 text-sm sm:text-base"
              >
                {t('viewAllListings')}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center py-16 sm:py-24 card">
            <div className="w-20 h-20 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">
              🏠
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-1">{t('noPropertiesFound')}</h3>
            <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">{t('noPropertiesYet')}</p>
            {isAuthenticated ? (
              <Link to="/dashboard" className="btn btn-primary px-8 py-3 font-bold">
                {t('postListing')}
              </Link>
            ) : (
              <Link to="/login" className="btn btn-primary px-8 py-3 font-bold">
                {t('loginAndPost')}
              </Link>
            )}
          </div>
        )}
      </section>

      {/* CTA Section — for non-authenticated users */}
      {!isAuthenticated && (
        <section className="bg-gradient-to-r from-primary-600 to-primary-800 py-12 sm:py-16 mt-6">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">{t('wantToSell')}</h2>
            <p className="text-primary-100 text-sm sm:text-base mb-8 max-w-2xl mx-auto">
              {t('wantToSellDescription')}
            </p>
            <div className="flex flex-wrap gap-4 justify-center text-white text-xs sm:text-sm font-semibold mb-6">
              <div className="flex items-center">
                <span className="text-green-400 mr-1.5">✓</span>
                {t('easyListing')}
              </div>
              <div className="flex items-center">
                <span className="text-green-400 mr-1.5">✓</span>
                {t('flexiblePlans')}
              </div>
              <div className="flex items-center">
                <span className="text-green-400 mr-1.5">✓</span>
                {t('millionsBuyers')}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 max-w-md mx-auto">
              <p className="text-primary-100 text-xs sm:text-sm">
                {t('tgBotHint')}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="py-12 sm:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-center text-gray-900 mb-8 sm:mb-12">{t('howItWorks')}</h2>

        <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
          {[
            {
              icon: '🔍',
              title: t('step1Title'),
              desc: t('step1Description'),
            },
            {
              icon: '🏠',
              title: t('step2Title'),
              desc: t('step2Description'),
            },
            {
              icon: '🤝',
              title: t('step3Title'),
              desc: t('step3Description'),
            },
          ].map((step, index) => (
            <div key={index} className="text-center card p-6 group hover:shadow-lg transition">
              <div className="w-14 h-14 bg-primary-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl group-hover:scale-110 transition-transform">
                {step.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
